/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef } from "react";

// react-router-dom components

// @mui material components
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/system";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";

// sweetalert2 components
import Swal from "sweetalert2";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";
import DataTable from "Tables/DataTable";
import LanguagesModal from "Modals/Languages";
import EditTranslations from "Modals/EditTranslations";
import CheckObjectDeletion from "Modals/CheckObjectDeletion";

// Soft UI Dashboard PRO React components
import SoftBadge from "components/SoftBadge";

import { getData, postData, putData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

import { useSoftUIController } from "context";

import Cookies from "js-cookie";

// Badges
const disabledTemplate = (
  <SoftBadge variant="contained" color="error" size="xs" badgeContent="Inactive" container />
);
const enabledTemplate = (
  <SoftBadge variant="contained" color="success" size="xs" badgeContent="Active" container />
);

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

let lastSavedData = {};
let hiddenLangsReset = [];
let platform_id = null;
let allLanguages = [];
let lastSportSelected = null;

/**
 *
 * @returns
 */
function ParticipantsTranslationsList() {
  const abortController = new AbortController();

  const [controller, dispatch] = useSoftUIController();
  const { platform } = controller;

  const [showEditModal, setCreateModal] = useState(false);
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [showLanguagesModal, setLanguagesModal] = useState(false);
  const [updateRecords, setUpdateRecords] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(0);
  const [templateCreatedId, setTemplateCreatedId] = useState(0);
  const [hiddenColumnsArray, setHiddenColumns] = useState(null);
  const [dataTableData, setDataTableData] = useState({
    columns: [
      {
        Header: "Sport",
        accessor: "sport",
        width: "150px",
      },
      {
        Header: "Original Name",
        accessor: "original",
        width: "150px",
        Cell: linkCell,
        editable: false,
      },
    ],
    rows: [],
  });
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [rowDataSelected, setRowDataSelected] = useState(null);
  const [initializing, setInitializing] = useState(false);
  const [sportSelected, setSportSelected] = useState(null);
  const [sports, setSports] = useState([]);
  const prevHiddenColumnsArray = useRef(null);
  const [updating, setUpdating] = useState(false);

  const userData = getUserData();
  let userId = userData.id;

  async function getInitData(status) {
    if (status == false) {
      let data = {
        columns: lastSavedData.columns ? [...lastSavedData.columns] : [...dataTableData.columns],
        rows: [],
      };
      return data;
    }

    const userData = getUserData();
    let userId = userData.id;
    //console.log("getInitData...");
    //if (status) console.log("from initData");
    let initTableData = {
      columns: [
        {
          Header: "Sport",
          accessor: "sport",
          width: "50px",
        },
        {
          Header: "Original Name",
          accessor: "original",
          width: "150px",
          Cell: linkCell,
          editable: false,
        },
      ],
      rows: [],
    };

    let langArray = [];
    let languagesEndpoint = `/api/v1/entities/availablelanguages`;
    let response = await getData(languagesEndpoint, {
      Authorization: getAuthorizationHeader(),
    });

    langArray = response.data.map((lang) => {
      return lang.name;
    });

    langArray.sort();
    let switchLanguages = [];
    let hiddenlanguages = [];
    allLanguages = [];
    let cookiesHiddenColumns = Cookies.get("settings-translations-participants-" + userId);

    langArray.forEach((lang) => {
      initTableData.columns.push(createHeader(lang));

      let checked = false;
      if (status && lang == "English") checked = true;

      if (cookiesHiddenColumns) {
        if (cookiesHiddenColumns.indexOf(lang.toLowerCase()) > -1) {
          checked = false;
        } else {
          checked = true;
        }
      }

      switchLanguages.push({ name: lang, checked: checked });
      allLanguages.push(lang);
      if (lang != "English" && hiddenlanguages.indexOf(lang.toLowerCase()) < 0)
        hiddenlanguages.push(lang.toLowerCase());
    });

    hiddenLangsReset = hiddenlanguages;

    if (status) {
      setDataTableData(initTableData);
      setHiddenColumns(hiddenLangsReset);
      setAvailableLanguages(switchLanguages);

      if (cookiesHiddenColumns) {
        setHiddenColumns(cookiesHiddenColumns.split(","));
      } else {
        setHiddenColumns(hiddenlanguages);
      }
    }

    return initTableData;
  }

  function createHeader(lang) {
    return {
      Header: lang,
      accessor: lang.toLowerCase(),
      width: "350px",
      Cell: editableCell,
    };
  }

  // Create an editable cell renderer
  function editableCell({ value, ...rest }) {
    return (
      <>
        <SoftBox
          className="translations-cell"
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          p={0}
        >
          {/* <SoftTypography>{value ? value : ""}</SoftTypography> */}
          <SoftInput
            key={
              "participant-" +
              rest.row.cells[1].value.toLowerCase().replace(" ", "_") +
              "-editable-lang-" +
              rest.column.Header.toLowerCase() +
              "-" +
              value
            }
            id={
              "participant-" +
              rest.row.cells[1].value.toLowerCase().replace(" ", "_") +
              "-editable-lang-" +
              rest.column.Header.toLowerCase()
            }
            defaultValue={value}
            // onChange={({ currentTarget }) => {
            //   //console.log(value, currentTarget.value);
            //   value = currentTarget.value;
            // }}
            onBlur={(e) => updateData(e, rest)}
            onKeyUp={(e) => updateData(e, rest)}
            size="small"
            style={{ width: "50px" }}
            className="editable-cell to-edit"
          />

          <HtmlTooltip placement="right" title={"Reset to Original Translation"}>
            <Button onClick={(e) => updateResetData(e, rest)} className="hidden">
              <Icon>rotate_left_outlined_icon</Icon>
            </Button>
          </HtmlTooltip>
        </SoftBox>
      </>
    );
  }

  // Create an editable cell renderer
  function linkCell({ value, ...rest }) {
    return (
      <>
        <HtmlTooltip placement="right" title={"Update all Translations"}>
          <Button onClick={(e) => handleEditOpenModal(e, rest)} className="to-edit">
            <SoftTypography
              component="span"
              variant="button"
              fontWeight="regular"
              color={"info"}
              opacity={0.8}
              sx={{ lineHeight: 0 }}
            >
              {value}
            </SoftTypography>
          </Button>
        </HtmlTooltip>
        <Icon className="hidden">edit_outlined_icon</Icon>
      </>
    );
  }

  async function updateResetData(e, rowData) {
    e.currentTarget.value = "reset";
    updateData(e, rowData);
  }

  async function updateData(e, rowData) {
    e.preventDefault();
    e.stopPropagation();
    setUpdating(true);
    if (updating) return;

    if (e._reactName == "onKeyUp" && e.code != "Enter") return;

    let tmpTableData = JSON.parse(JSON.stringify(lastSavedData));

    let row = lastSavedData.rows.find((item) => item.original == rowData.row.cells[1].value);

    if (row[rowData.column.Header.toLowerCase()] == e.currentTarget.value) return;

    let updateParticipantsTranslationsEndpoint = `/api/v1/translations/clientparticipants`;
    let data = {
      lang: rowData.column.Header,
      translation: e.currentTarget.value,
      original: rowData.row.cells[1].value,
      platform_id: platform_id,
      uni_id: rowData.row.original.uni_id,
    };

    let response = null;
    if (data.lang && data.translation && data.original && data.platform_id) {
      setUpdating(true);
      response = await putData(updateParticipantsTranslationsEndpoint, data, {
        Authorization: getAuthorizationHeader(),
      });
    }

    if (response) {
      setUpdating(false);
      initData(false, false);
    }
  }

  /**
   *
   */
  const handleLanguages = (languages) => {
    const userData = getUserData();
    let userId = userData.id;
    setHiddenColumns(languages);
    if (showLanguagesModal) setLanguagesModal(false);
    if (!showLanguagesModal) setLanguagesModal(true);

    Cookies.set("settings-translations-participants-" + userId, languages, {
      expires: 7,
      path: "/",
      sameSite: "Lax",
    });
  };

  /**
   *
   * @param {*} update
   * @param {*} name
   */
  const handleCloseLanguagesModal = async (update, name) => {
    if (showLanguagesModal) setLanguagesModal(false);
    if (!showLanguagesModal) setLanguagesModal(true);
  };

  /**
   *
   * @param {*} update
   * @param {*} name
   */
  const handleEditCloseModal = async (update, name) => {
    if (update) {
      //console.log("Creation completed, please Update!");
      setRowDataSelected(null);
      initData(false, true);
    } else {
      setRowDataSelected(null);
      //console.log("Creation canceled, do nothing!");
    }
    setCreateModal(false);
  };

  /**
   *
   * @param {*} update
   * @param {*} name
   */
  const handleEditOpenModal = async (e, rowData) => {
    e.preventDefault();
    setCreateModal(false);
    setRowDataSelected(null);
    let selectedItem = rowData.row.original; //rowData.data.find((item) => item.original == e.currentTarget.textContent);
    let selectedPlatformId = platform_id;

    let getParticipantsTranslationsEndpoint = `/api/v1/translations/clientparticipantslang`;
    let response = await postData(
      getParticipantsTranslationsEndpoint,
      {
        original: selectedItem.original,
        sport: selectedItem.sport,
        platform_id: selectedPlatformId,
        uni_id: selectedItem.uni_id,
      },
      {
        Authorization: getAuthorizationHeader(),
      },
      null,
      abortController ? { signal: abortController.signal } : null
    );

    let entries = Object.entries(response.data);
    entries.forEach((entry) => {
      let translations = entry[1];
      Object.keys(translations).forEach((key) => {
        if (key != "sport" && key != "region" && key != "original")
          selectedItem[key.toLowerCase()] = translations[key];
        selectedItem[1][key] = translations[key];
      });
    });

    setRowDataSelected(selectedItem);
    setLanguagesModal(false);
    setCreateModal(true);
  };

  /**
   *
   * @param {*} update
   * @param {*} id
   */
  const handleDeleteCloseModal = async (update, id) => {
    if (update) {
      //console.log("Delete confirmed, please Update!");
      // let setTemplatesEndpoint = `/api/v1/users/${id}`;
      // let response = await delData(setTemplatesEndpoint, {
      //   Authorization: getAuthorizationHeader(),
      // });
      // if (response) {
      //   deleteRecord(id);
      //   initData();
      // }
    } else {
      //console.log("Delete canceled, do nothing!");
    }
    setDeleteModal(false);
  };

  /**
   *
   */
  async function initData(reset, loader) {
    if (!platform || platform == "") return;
    if (initializing) return;
    const userData = getUserData();
    let userId = userData.id;
    setInitializing(true);
    let cookiesHiddenColumns = Cookies.get("settings-translations-participants-" + userId);
    if (loader) {
      Swal.fire({
        title: "We proccess your request! ",
        html: "Wait for few seconds...",
        timerProgressBar: true,
        didOpen: async () => {
          Swal.showLoading();
          const userData = getUserData();
          let userId = userData.id;
          let data = await getInitData(reset);
          let queryStringLanguages = "English";
          let availableLanguages = [];
          data.columns.forEach((column) => {
            if (
              column.accessor != "region" &&
              column.accessor != "sport" &&
              column.accessor != "original"
            )
              availableLanguages.push(column.Header);
          });
          if (availableLanguages.length == 0) availableLanguages = [...allLanguages];
          if (hiddenColumnsArray) {
            queryStringLanguages = availableLanguages
              .filter((item) => !hiddenColumnsArray.includes(item.toLowerCase()))
              .join(",");
          } else {
            let cookiesHiddenColumnsArray = cookiesHiddenColumns
              ? cookiesHiddenColumns.split(",")
              : [];
            queryStringLanguages = availableLanguages
              .filter((item) => !cookiesHiddenColumnsArray.includes(item.toLowerCase()))
              .join(",");
          }
          if (reset) {
            setDataTableData(data);
            //setHiddenColumns(hiddenLangsReset);
          }
          let selectedPlatformId = platform_id;
          let getParticipantsTranslationsEndpoint = `/api/v1/translations/clientparticipantslang/${selectedPlatformId}?lang=${queryStringLanguages}`;
          let response = await getData(
            getParticipantsTranslationsEndpoint,
            {
              Authorization: getAuthorizationHeader(),
            },
            null,
            abortController ? { signal: abortController.signal } : null
          );
          if (!response) {
            Swal.clickDeny();
          } else {
            if (response.status === 200) {
              let entries = [];
              let languages = [];
              let hiddenlanguages = [];
              let availableSports = [{ label: "All", value: "All" }];
              if (response != null) {
                entries = Object.entries(response.data);
                entries.map((el) => {
                  el.original = el[1].original;
                  el.sport = el[1].sport;
                  for (const lang in el[1]) {
                    if (lang == "region" || lang == "original") continue;
                    if (lang == "sport") {
                      let sportFound = availableSports.find((item) => item.label == el.sport);
                      if (!sportFound) availableSports.push({ label: el.sport, value: el.sport });
                      continue;
                    }
                    if (Object.hasOwnProperty.call(el[1], lang)) {
                      if (languages.indexOf(lang) < 0) languages.push(lang);
                      const translation = el[1][lang];
                      el[lang.toLowerCase()] = translation;
                    }
                  }
                  availableLanguages.forEach((lang) => {
                    if (!el[lang.toLowerCase()]) {
                      el[lang.toLowerCase()] = null;
                    }
                    if (
                      lang.toLowerCase() != "english" &&
                      hiddenlanguages.indexOf(lang.toLowerCase()) < 0
                    )
                      hiddenlanguages.push(lang.toLowerCase());
                    if (!el[1][lang.charAt(0).toUpperCase() + lang.slice(1)])
                      el[1][lang.charAt(0).toUpperCase() + lang.slice(1)] = null;
                  });
                });
              } else {
              }
              availableSports.sort((a, b) => (a.label > b.label ? 1 : -1));
              setSports(availableSports);

              if (reset) {
                if (cookiesHiddenColumns) {
                  setHiddenColumns(cookiesHiddenColumns.split(","));
                } else {
                  setHiddenColumns(hiddenlanguages);
                }
              }
              data.rows = response && response.data ? entries : data.rows;
              //console.log("Init Data");
              setDataTableData(data);
              lastSavedData = data;
              setInitializing(false);
              if (!lastSportSelected) {
                setSportSelected({ label: "All", value: "All" });
              } else {
                handleSelectionValueChange(lastSportSelected);
              }
              Swal.clickConfirm();
            }
          }
        },
        willClose: () => {},
        allowOutsideClick: false,
      });
    } else {
      const userData = getUserData();
      let userId = userData.id;
      let data = await getInitData(reset);
      let queryStringLanguages = "English";
      let availableLanguages = [];
      data.columns.forEach((column) => {
        if (
          column.accessor != "region" &&
          column.accessor != "sport" &&
          column.accessor != "original"
        )
          availableLanguages.push(column.Header);
      });
      if (availableLanguages.length == 0) availableLanguages = [...allLanguages];
      if (hiddenColumnsArray) {
        queryStringLanguages = availableLanguages
          .filter((item) => !hiddenColumnsArray.includes(item.toLowerCase()))
          .join(",");
      } else {
        let cookiesHiddenColumnsArray = cookiesHiddenColumns ? cookiesHiddenColumns.split(",") : [];
        queryStringLanguages = availableLanguages
          .filter((item) => !cookiesHiddenColumnsArray.includes(item.toLowerCase()))
          .join(",");
      }

      if (queryStringLanguages == "") {
        let cookiesHiddenColumnsArray = cookiesHiddenColumns ? cookiesHiddenColumns.split(",") : [];
        queryStringLanguages = availableLanguages
          .filter((item) => !cookiesHiddenColumnsArray.includes(item.toLowerCase()))
          .join(",");
        if (queryStringLanguages == "") queryStringLanguages = "English";
      }

      if (reset) {
        setDataTableData(data);
        //setHiddenColumns(hiddenLangsReset);
      }

      let selectedPlatformId = platform_id;
      let getParticipantsTranslationsEndpoint = `/api/v1/translations/clientparticipantslang/${selectedPlatformId}?lang=${queryStringLanguages}`;

      let response = await getData(
        getParticipantsTranslationsEndpoint,
        {
          Authorization: getAuthorizationHeader(),
        },
        null,
        abortController ? { signal: abortController.signal } : null
      );

      if (!response) {
      } else {
        if (response.status === 200) {
          let entries = [];
          let languages = [];
          let hiddenlanguages = [];
          let availableSports = [{ label: "All", value: "All" }];
          if (response != null) {
            entries = Object.entries(response.data);
            entries.map((el) => {
              el.original = el[1].original;
              el.sport = el[1].sport;
              for (const lang in el[1]) {
                if (lang == "region" || lang == "original") continue;
                if (lang == "sport") {
                  let sportFound = availableSports.find((item) => item.label == el.sport);
                  if (!sportFound) availableSports.push({ label: el.sport, value: el.sport });
                  continue;
                }
                if (Object.hasOwnProperty.call(el[1], lang)) {
                  if (languages.indexOf(lang) < 0) languages.push(lang);
                  const translation = el[1][lang];
                  el[lang.toLowerCase()] = translation;
                }
              }
              availableLanguages.forEach((lang) => {
                if (!el[lang.toLowerCase()]) {
                  el[lang.toLowerCase()] = null;
                }
                if (
                  lang.toLowerCase() != "english" &&
                  hiddenlanguages.indexOf(lang.toLowerCase()) < 0
                )
                  hiddenlanguages.push(lang.toLowerCase());
                if (!el[1][lang.charAt(0).toUpperCase() + lang.slice(1)])
                  el[1][lang.charAt(0).toUpperCase() + lang.slice(1)] = null;
              });
            });
          } else {
          }

          availableSports.sort((a, b) => (a.label > b.label ? 1 : -1));
          setSports(availableSports);

          if (reset) {
            if (cookiesHiddenColumns) {
              setHiddenColumns(cookiesHiddenColumns.split(","));
            } else {
              setHiddenColumns(hiddenlanguages);
            }
          }
          data.rows = response && response.data ? entries : data.rows;

          //console.log("Init Data");
          setDataTableData(data);
          lastSavedData = data;
          setInitializing(false);
          if (!lastSportSelected) {
            setSportSelected({ label: "All", value: "All" });
          } else {
            handleSelectionValueChange(lastSportSelected);
          }
        }
      }
    }
  }

  /**
   *
   * @param {*} id
   */
  function deleteRecord(id) {
    //console.log("deleteRecord:", id);
    let initData = { ...dataTableData };
    let initDataRows = [...dataTableData.rows];

    let indexToRemove = initDataRows.findIndex((template) => template.id === id);
    initDataRows.splice(indexToRemove, 1);

    initData.rows = initDataRows;
    setDataTableData(initData);
  }

  const handleSelectionValueChange = (item) => {
    setSportSelected(item);
    lastSportSelected = item;

    let columns = [...lastSavedData.columns];
    let rows = [...lastSavedData.rows];

    if (item.label != "All") rows = rows.filter((row) => row.sport === item.label);

    let data = { columns: columns, rows: rows };

    setDataTableData(data);
  };

  const handleTableDataChanges = async ({ key, value, extra }) => {
    //console.log(key, ":", value, extra);
    if (key == "refresh") {
      initData(true, true);
    }
  };

  useEffect(() => {
    if (platform) platform_id = platform.value;
    //console.log("platform.label change: ", hiddenColumnsArray);
  }, [platform.label]);

  useEffect(() => {
    if (hiddenColumnsArray) {
      initData(true, true);
    }
  }, [platform_id]);

  useEffect(() => {
    //console.log("hiddenColumnsArray change: ", hiddenColumnsArray);
    if (
      hiddenColumnsArray &&
      !initializing &&
      JSON.stringify(prevHiddenColumnsArray.current) != JSON.stringify(hiddenColumnsArray)
    ) {
      initData(false, true);
    }
    prevHiddenColumnsArray.current = hiddenColumnsArray;
  }, [hiddenColumnsArray]);

  useEffect(() => {
    getInitData(true);
    lastSportSelected = null;
    return () => {
      setCreateModal(false);
      // console.log("living page...");
      if (abortController) {
        // console.log("abortController landing page");
        abortController.abort();
      }
    };
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LanguagesModal
        languages={availableLanguages}
        showModal={showLanguagesModal}
        signalCloseModal={handleCloseLanguagesModal}
        setAvailableLanguages={handleLanguages}
      ></LanguagesModal>
      <EditTranslations
        showModal={showEditModal}
        signalCloseModal={handleEditCloseModal}
        data={rowDataSelected}
        entity="participant"
      ></EditTranslations>
      <CheckObjectDeletion
        referenceId={recordToDelete}
        reference={"users"}
        showModal={showDeleteModal}
        signalCloseModal={handleDeleteCloseModal}
      ></CheckObjectDeletion>
      <SoftBox my={3}>
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                Participants Translations
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                Here you can Manage all the available translations for Participants
              </SoftTypography>
            </SoftBox>
            <Stack spacing={1} direction="row">
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                onClick={handleCloseLanguagesModal}
              >
                Language Columns
              </SoftButton>
            </Stack>
          </SoftBox>
          <SoftBox p={3} sx={{ paddingTop: "0px !important" }}>
            <Grid container spacing={3}>
              <Grid item xs={3} md={3}>
                <SoftSelect
                  value={sportSelected}
                  id="trans-sport-filter"
                  size="small"
                  options={sports}
                  onChange={handleSelectionValueChange}
                />
              </Grid>
            </Grid>
          </SoftBox>
          <DataTable
            table={dataTableData}
            entriesPerPage={{
              defaultValue: 10,
              entries: [5, 10, 15, 20, 25],
            }}
            hiddenColumns={hiddenColumnsArray}
            canSearch
            refreshEnabled={true}
            informParent={handleTableDataChanges}
            bodyClassName="participant-editable"
            headerClassName="participant-editable"
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default ParticipantsTranslationsList;
